import { useState } from 'react'
import MetaTags from '../components/common/MetaTags'
import ResumeUploadCard from '../components/common/ResumeUploadCard'
import { useProfileContext } from '../components/layout/AuthenticatedLayout'
import EducationSection from '../components/profile/EducationSection'
import ProfileBasicInfo from '../components/profile/ProfileBasicInfo'
import ProjectsSection from '../components/profile/ProjectsSection'
import ResumeUpload from '../components/profile/ResumeUpload'
import WorkExperienceSection from '../components/profile/WorkExperienceSection'
import type { Profile } from '../types'

export default function EditDetailsPage() {
  const { profile, setProfile } = useProfileContext()
  const [successMessage, setSuccessMessage] = useState('')
  const [activeTab, setActiveTab] = useState('basic')

  const showSuccess = (message: string) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const handleProfileUpdate = (updatedProfile: Profile) => {
    setProfile(updatedProfile)
    showSuccess('Changes saved!')
  }

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto">
        <MetaTags
          title="Edit Details | profile.work"
          description="Update your professional profile information."
        />
        <ResumeUploadCard
          title="No profile to edit"
          subtitle="Upload your resume to create your professional profile."
          icon={
            <svg
              className="w-8 h-8 text-black/40"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          }
        />
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      <MetaTags
        title="Edit Details | profile.work"
        description="Update your professional profile information, work experience, and education."
      />

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 z-50 animate-in slide-in-from-bottom-2">
          <div className="px-4 py-3 bg-black/90 backdrop-blur-xl rounded-xl shadow-lg">
            <p className="text-sm text-white flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {successMessage}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1
          className="text-2xl sm:text-3xl text-black mb-2"
          style={{ fontFamily: "'Instrument Serif', serif" }}
        >
          Edit Details
        </h1>
        <p className="text-black/60">
          Update your profile information, work experience, education, and projects.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        {/* Mobile dropdown */}
        <div className="grid grid-cols-1 sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            aria-label="Select a section"
            className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-black outline-1 -outline-offset-1 outline-black/20 focus:outline-2 focus:-outline-offset-2 focus:outline-black"
          >
            {[
              { id: 'basic', label: 'Basic Info' },
              { id: 'resume', label: 'Resume' },
              { id: 'experience', label: 'Experience' },
              { id: 'education', label: 'Education' },
              { id: 'projects', label: 'Projects' },
            ].map((tab) => (
              <option key={tab.id} value={tab.id}>
                {tab.label}
              </option>
            ))}
          </select>
          <svg
            className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-black/50"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.22 6.22a.75.75 0 0 1 1.06 0L8 8.94l2.72-2.72a.75.75 0 1 1 1.06 1.06l-3.25 3.25a.75.75 0 0 1-1.06 0L4.22 7.28a.75.75 0 0 1 0-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        {/* Desktop tabs */}
        <div className="hidden sm:block">
          <div className="border-b border-black/10">
            <nav aria-label="Tabs" className="-mb-px flex space-x-8">
              {[
                { id: 'basic', label: 'Basic Info' },
                { id: 'resume', label: 'Resume' },
                { id: 'experience', label: 'Experience' },
                { id: 'education', label: 'Education' },
                { id: 'projects', label: 'Projects' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                  className={`border-b-2 px-1 py-4 text-sm font-medium whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-black text-black'
                      : 'border-transparent text-black/50 hover:border-black/20 hover:text-black/70'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-6">
        {activeTab === 'basic' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
            <ProfileBasicInfo profile={profile} onUpdate={handleProfileUpdate} />
          </div>
        )}

        {activeTab === 'resume' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden p-6">
            <h2
              className="text-lg font-semibold text-black mb-4"
              style={{ fontFamily: "'Instrument Serif', serif" }}
            >
              Resume
            </h2>
            <ResumeUpload
              hasResume={profile.has_resume}
              allowResumeDownload={profile.allow_resume_download}
              onUpdate={handleProfileUpdate}
            />
          </div>
        )}

        {activeTab === 'experience' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
            <WorkExperienceSection
              experiences={profile.work_experiences}
              onUpdate={(experiences) => {
                setProfile({ ...profile, work_experiences: experiences })
                showSuccess('Work experience updated!')
              }}
            />
          </div>
        )}

        {activeTab === 'education' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
            <EducationSection
              educations={profile.educations}
              onUpdate={(educations) => {
                setProfile({ ...profile, educations })
                showSuccess('Education updated!')
              }}
            />
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-xl border border-black/10 overflow-hidden">
            <ProjectsSection
              projects={profile.projects}
              onUpdate={(projects) => {
                setProfile({ ...profile, projects })
                showSuccess('Projects updated!')
              }}
            />
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 pb-8 text-center">
          <p className="text-sm text-black/50">
            Changes are saved automatically when you click Save on each section.
          </p>
        </div>
      </div>
    </div>
  )
}
